import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ReplaySubject, Subject, Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, takeUntil, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Profile, User } from 'app/models';
import { SelectedUserService } from '../selected-user-service/selected-user.service';
import { SnackBarService } from '../snackbar-service/snackbar.service';
import { SnackbarType } from 'app/constants/Enums';
import { ApiUrl } from 'app/constants/ApiUrls';

@Injectable({
  providedIn: 'root'
})
export class UsersService implements OnDestroy {
  constructor(
    private _http: HttpClient,
    private _translateService: TranslateService,
    private _selectedUserService: SelectedUserService,
    public _snackbarService: SnackBarService
  ) {}
  readonly amUsersUrl = `${environment.apiUrl}/api/Users`;
  readonly amUserUrl = `${environment.apiUrl}/api/User`;
  readonly amNewUsersUrl = `${environment.apiUrl}/api/NewUsers`;
  readonly branchAdminEndPoint = `${environment.apiUrl}/api/v3/Users`;

  private readonly usersSubject: BehaviorSubject<Profile[]> = new BehaviorSubject<Profile[]>(null);
  private readonly companyUsersSubject: BehaviorSubject<Profile[]> = new BehaviorSubject<Profile[]>(null);
  private readonly inactiveCompanyUsersSubject: Subject<Profile[]> = new Subject<Profile[]>();
  private readonly profileUserSubject = new ReplaySubject<User>();
  public user$ = this.profileUserSubject.asObservable();
  public users$: Observable<any> = this.usersSubject.asObservable();
  public companyUsers$: Observable<
    any
  > = this.companyUsersSubject.asObservable();

  public inactiveCompanyUsers$: Observable<
    any
  > = this.inactiveCompanyUsersSubject.asObservable();
  private _error: ReplaySubject<string> = new ReplaySubject<string>();
  public error$: Observable<string> = this._error.asObservable();
  private onDestroy$ = new Subject<void>();
  public usersSearchString = '';
  public internalOnly = false;
  public appId;
  public isInternal = true;
  public url: string;
  public loading: boolean;
  readonly internalSearchUrl = `${environment.apiUrl}/api/v2/Users`;
  public userList: User[];

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  public clearCompanyUser() {
    this.companyUsersSubject.next(null);
  }

  getUsersByCompanyId(companyId: string, isActive: boolean) {
    this.loading = true;
    let url = ApiUrl.companies + `/${companyId}/Profiles?isActive=${isActive}`;
    this._http
      .get(url)
      .pipe(catchError(error => this._handleUsersError(error)))
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this._error.next(data.errorMessage);
        } else {
          this._error.next(null);
          data.forEach(e => {
            if(!e.name){
              e.name = e.firstName + ' ' + e.lastName;
            }
          });
          if (isActive) {
            this.companyUsersSubject.next(data);
          } else {
            this.inactiveCompanyUsersSubject.next(data);
          }
          this.loading = false;
        }
      });
  }

  getTopTenUsers() {
    this.loading = true;
    const url = `${ApiUrl.usersV2}/Top10`;
    this._http
      .get(url)
      .pipe(catchError(error => this._handleUsersError(error)))
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this._error.next(data.errorMessage);
        } else {
          this._error.next(null);
          this.usersSubject.next(data);
          this.loading = false;
        }
      });
  }
  deleteUserById(url:string, showSnackbar: boolean = false, deleteUserFlag: boolean = false) {
    this._http
        .delete(url)
        .pipe(catchError(error => this._handleUsersError(error)))
        .subscribe((data: any) => {
          if (data) {
            if (data.errorMessage) {
              let errorTitle: string;
              this._translateService
                .get('SNACKBAR.error')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (errorTitle = value));
              this._snackbarService.showSnackbar(
                SnackbarType.error,
                errorTitle,
                data.errorMessage
              );
            }
            this._error.next(null);
          } else {
            this._error.next(null);
            if(deleteUserFlag)
              this.getTopTenUsers();
            if (showSnackbar) {
              let successTitle: string;
              let successMsg: string;
              this.getTopTenUsers();
              this._translateService
                .get('SNACKBAR.success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (successTitle = value));
              this._translateService
                .get('SNACKBAR.user_remove_success')
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((value) => (successMsg = value));
              this._snackbarService.showSnackbar(
                SnackbarType.success,
                successTitle,
                successMsg
              );
            }
          }
        });
    
  }
  deleteUsers(users: any) {
    users.forEach((user, i) => {
      const id = user.id;
      const url = `${ApiUrl.usersV3}/${id}`;
      this.deleteUserById(url, i === users.length - 1, false)
    });
    this.getTopTenUsers();
  }
  deleteUser(id) {
    const url = `${ApiUrl.usersV3}/${id}`;
    this.deleteUserById(url, false, true)
  }
  
  addRemoveAdmin(id: string, isAdmin: boolean, companyAccountId?: string): Observable<boolean> {
    let url = `${ApiUrl.usersV3}/${id}/CompanyAdmin?addUserAdmin=${isAdmin}`;
    
    return  this._http.patch(url, '').pipe(
      takeUntil(this.onDestroy$),
      map(data => {
        this._selectedUserService.getSelectedUser(id);
        if (companyAccountId) {
          this.getUsersByCompanyId(companyAccountId, true);
        }
        return true;
      }),
      catchError(error => {
        this._handleUsersError(error);
        return of(false);
      })
    )
  }

  getUserById(id) {
    const url = `${ApiUrl.usersV3}/${id}`;
    this._http
      .get(url)
      .pipe(catchError(error => this._handleUsersError(error)))
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this._error.next(data.errorMessage);
        } else {
          this._error.next(null);
          if (data) {
            let user = data;
            user.initials = this.getInitials(data.name);
            this.profileUserSubject.next(user);
          }
        }
      });
  }

  searchUsers(query: string, appId: string = ''): void {
    this.loading = true;
    this.usersSearchString = query;
    this.appId = appId;
    if (this.usersSearchString) {
      this.url = `${this.internalSearchUrl}?name=${this.usersSearchString}`;
    }
    this._http
      .get(this.url)
      .pipe(catchError(error => this._handleUsersError(error)))
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this.usersSubject.next(null);

          this._error.next(data.errorMessage);
        } else {
          this._error.next(null);

          this.usersSubject.next(data);
          this.loading = false;
        }
      });
  }

  private _handleUsersError(error: HttpErrorResponse) {
    let errorMsg: string;
    this.loading = false;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[users-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[users-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }

    if(error.status == 404){
      return of([]);
    }
    return of({ errorMessage: errorMsg });
  }

  addBranchAdmins(branchId: string, auth0Ids: string[]): Observable<boolean> {
    return this._http.patch(`${this.branchAdminEndPoint}/${branchId}/BranchAdmins`, auth0Ids).pipe(
      map(() => true), 
      catchError((error) =>{this._handleUsersError(error); throw new Error(error); }) 
    );
  }

  removeBranchAdmins(branchId: string, auth0Ids: string[]): Observable<boolean>{
    return this._http.delete(`${this.branchAdminEndPoint}/${branchId}/BranchAdmins`, { body: auth0Ids }).pipe(
      map(() => true),
      catchError((error) => { this._handleUsersError(error); throw new Error(error); })
    );
  }

  getInitials(name: string): string {
    const nameParts = name.trim().split(' ').filter(part => part !== '');

    const firstInitial = nameParts[0]?.charAt(0).toUpperCase() || '';
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0).toUpperCase() : '';

    return firstInitial + lastInitial;
}
}
